// TODO: remove with https://github.com/combinaut/sparkle/issues/32203 once formula editing uses an sp app.
import { useEventListener } from "@vueuse/core";
import { settleMount } from "#ce/framework/mount-ready";

const initialized = new WeakSet();

/**
 * Attaches formula suggestions within `root`, waiting for each resource control to mount.
 *
 * @param {ParentNode} root
 * @returns {Promise<() => void>} A disposer that removes resource listeners and suggestions and permits reinitialization.
 */
export async function initializeFormulaSuggestions(root) {
  const disposers = [];

  for (const config of root?.querySelectorAll('[data-behaviour~="formula-suggestions"]') || []) {
    if (initialized.has(config)) {
      continue;
    }

    initialized.add(config);

    const form = config.closest("form");
    const target = form?.querySelector(config.dataset.formulaTarget);
    const resource = form?.querySelector('[name="tribute_resource_type"], [name="resource_type"]');

    if (resource && (!(await settleMount(resource)) || !resource.isConnected)) {
      initialized.delete(config);

      continue;
    }

    if (!target || !config.isConnected) {
      initialized.delete(config);

      continue;
    }

    const suggestions = JSON.parse(config.dataset.formulaSuggestions);
    const tribute = new window.Tribute({
      autocompleteMode: true,
      requireLeadingSpace: false,
      replaceTextSuffix: "",
      allowSpaces: true,
      autocompleteSeparator: /[\s(,]+/,
      noMatchTemplate: "",
      selectTemplate: selectFormulaSuggestion,
      searchOpts: { caseSensitive: false },
      menuItemTemplate: formulaSuggestionMenuItem,
      values: suggestions[resource?.value] || Object.values(suggestions)[0] || [],
    });

    tribute.attach(target);

    function updateFormulaResource() {
      if (resource.dataset.formulaUpdateTarget) {
        const updateTarget = form.querySelector(resource.dataset.formulaUpdateTarget);

        if (updateTarget) {
          updateTarget.value = resource.value;
        }
      }

      tribute.append(0, suggestions[resource.value] || [], true);
    }

    let stopResourceListener;

    if (resource) {
      stopResourceListener = useEventListener(resource, "change", updateFormulaResource);
      updateFormulaResource();
    }

    function disposeConfig() {
      stopResourceListener?.();
      tribute.detach(target);
      initialized.delete(config);
    }

    disposers.push(disposeConfig);
  }

  return function disposeFormulaSuggestions() {
    disposers.splice(0).forEach((dispose) => dispose());
  };
}

function selectFormulaSuggestion(item) {
  if (typeof item === "undefined") {
    return null;
  }

  if (this.range.isContentEditable(this.current.element)) {
    return `<span data-value="${item.original.value}" contenteditable="false"><a>${item.original.key}</a></span>`;
  }

  return item.original.value;
}

function formulaSuggestionMenuItem(item) {
  return `<div class="suggestion-item-${item.original.type}">${item.string}</div>`;
}
